import React from "react";
import ReCAPTCHA from "react-google-recaptcha";

interface Props {
  captchaError?: string;
  email: string;
  errors: {
    email?: string;
    first_name?: string;
  };
  // Surfaces that want their own pitch pass a replacement heading; everything
  // else gets the default one.
  heading?: React.ReactNode;
  // Overridden only where the wording is editable in admin.
  promise?: string | null;
  buttonLabel?: string;
  firstName: string;
  isSaving: boolean;
  onCaptchaChange: (token: string | null) => void;
  onFieldChange: (field: string, value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  subscribed: boolean;
  username: string;
}

const renderFieldError = (message?: string) => {
  if (!message) return null;

  return (
    <div className="inline-error" role="alert">
      {message}
    </div>
  );
};

// No id on the heading: this renders on the home page, the blog popup and the
// inline blog band, and an open popup would duplicate it. Each surface labels
// its own landmark with aria-label instead.
// Both newsletters are named and dated on purpose. "Tuesdays and Fridays" reads
// as an appointment; "two emails a week" reads as a cost, and hiding the
// frequency altogether just moves the objection to the first unsubscribe.
const NewsletterHeading = () => (
  <>
    <h2>
      Let us nourish <em>your noggin</em>
    </h2>
    <p className="lead">
      Receive one useful idea every Tuesday. The week's best reads every Friday.
      {/* Its own line, because the discount is the only immediate reward here
          and it was getting lost on the end of a sentence about email. A span
          rather than a second paragraph: the blog band lays this out on a
          two-row grid that an extra child would unbalance. */}
      <span className="lead-offer">
        Plus <strong>10% off your next purchase</strong>, right away.
      </span>
    </p>
  </>
);

const SubscribedMessage = () => (
  <div className="newsletter-confirm" role="status" aria-live="polite">
    <span className="stamp" aria-hidden="true">
      ✉️
    </span>
    <h3>
      Right then,{" "}
      <em>
        <span>you</span>
      </em>
      . You're in.
    </h3>
    <p className="punchline">
      We'll send you something interesting on Friday morning, around the time
      you've stopped pretending to read your inbox properly.
    </p>
  </div>
);

const SubscriptionWrapper: React.FC<Props> = ({
  captchaError,
  email,
  errors,
  heading,
  promise,
  buttonLabel,
  firstName,
  isSaving,
  onCaptchaChange,
  onFieldChange,
  onSubmit,
  subscribed,
  username,
}) => {
  if (subscribed) return <SubscribedMessage />;

  return (
    <>
      {heading || <NewsletterHeading />}
      <form aria-label="Newsletter signup" onSubmit={onSubmit}>
        <div className="row-2">
          <div className={`field ${errors.first_name ? "error" : ""}`}>
            <label htmlFor="newsletter-firstname" className="sr-only">
              First name
            </label>
            <input
              type="text"
              id="newsletter-firstname"
              name="first_name"
              placeholder="First name"
              autoComplete="given-name"
              value={firstName}
              onChange={(event) =>
                onFieldChange("firstName", event.target.value)
              }
              required
            />
            {renderFieldError(errors.first_name)}
          </div>

        </div>

        <div className="field spc_username" aria-hidden="true">
          <input
            type="text"
            placeholder="Your username"
            name="username"
            autoComplete="off"
            tabIndex={-1}
            value={username}
            onChange={(event) => onFieldChange("username", event.target.value)}
          />
        </div>

        <div className={`field ${errors.email ? "error" : ""}`}>
          <label htmlFor="newsletter-email" className="sr-only">
            Email address
          </label>
          <input
            type="email"
            id="newsletter-email"
            name="email"
            placeholder="Email address"
            autoComplete="email"
            value={email}
            onChange={(event) => onFieldChange("email", event.target.value)}
            required
          />
          {renderFieldError(errors.email)}
        </div>

        <div className="recaptcha">
          <ReCAPTCHA
            sitekey={process.env.RECAPTCHA_SITE_TOKEN}
            onChange={onCaptchaChange}
          />
          {renderFieldError(captchaError)}
        </div>

        <button className="btn-newsletter" type="submit" disabled={isSaving}>
          {buttonLabel || "Send me my 10%"}
        </button>
      </form>
      {/* Social proof sits under the button, where the decision actually gets
          made. It replaces the old "no spam, we promise", which only planted the
          idea it was denying. */}
      <p className="promise">
        {promise === undefined
          ? "Join 17,489+ curious people. Unsubscribe any time."
          : `${promise ? `${promise} ` : ""}Unsubscribe any time.`}
      </p>
    </>
  );
};

export default SubscriptionWrapper;
